import styled from 'styled-components';
import {
  getTextType,
  backgroundShape,
  getSpace,
  mq,
  getGridSpan
} from '../../../stylesheets/tools/index';

export const StyledMap = styled.div`
  position: relative;

  .map__map {
    position: relative;
  }

  .map__wrapper {
    border: 2px solid #000;
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    overflow: hidden;
    left: 20px;

    ${mq('m')} {
      left: 30px;
    }

    ${mq('xl')} {
      left: 60px;
    }

    ${mq('xl')} {
      left: ${getGridSpan('1', 'spread')};
    }
  }

  .map__background {
    position: absolute;
    width: 100%;
    height: 0;
    top: 42px;
    padding-bottom: 56.25%;
    background: ${props => props.theme.siteTheme.primary};
    right: ${getGridSpan('1', 'spread')};

    ${mq('s')} {
      top: 30px;
    }

    ${mq('l')} {
      top: 45px;
    }

    ${mq('xl')} {
      right: 0;
    }
  }

  .map__popup {
    p {
      margin: ${getSpace('5px')} 0;
    }
  }

  /* required styles */
  .leaflet-pane,
  .leaflet-tile,
  .leaflet-marker-icon,
  .leaflet-marker-shadow,
  .leaflet-tile-container,
  .leaflet-pane > svg,
  .leaflet-pane > canvas,
  .leaflet-zoom-box,
  .leaflet-image-layer,
  .leaflet-layer {
    position: absolute;
    left: 0;
    top: 0;
  }

  .leaflet-container {
    position: absolute;
    overflow: hidden;
    width: 100%;
    height: 100%;
    -webkit-tap-highlight-color: transparent;
    background: #fff;

    outline: 0;
    top: 0;
    left: 0;

    a {
      -webkit-tap-highlight-color: rgba(51, 181, 229, 0.4);
      color: #0078a8;

      &.leaflet-active {
        outline: 2px solid orange;
      }
    }

    img {
      position: absolute;
    }
  }

  .leaflet-tile,
  .leaflet-marker-icon,
  .leaflet-marker-shadow {
    user-select: none;
    -webkit-user-drag: none;
  }

  .leaflet-tile {
    filter: inherit;
    visibility: hidden;
  }

  .leaflet-tile-container {
    pointer-events: none;
  }

  .leaflet-tile-loaded {
    visibility: inherit;
  }

  /* Prevents IE11 from highlighting tiles in blue */
  .leaflet-tile::selection {
    background: transparent;
  }

  /* Safari renders non-retina tile on retina better with this, but Chrome is worse */
  .leaflet-safari .leaflet-tile {
    image-rendering: optimize-contrast;
  }

  /* hack that prevents hw layers "stretching" when loading new tiles */
  .leaflet-safari .leaflet-tile-container {
    width: 1600px;
    height: 1600px;
    transform-origin: 0 0;
  }

  .leaflet-marker-icon,
  .leaflet-marker-shadow {
    display: block;
  }

  /* workaround for https://bugzilla.mozilla.org/show_bug.cgi?id=888319 */
  .leaflet-overlay-pane svg {
    user-select: none;
  }

  .leaflet-map-pane svg {
    z-index: 20;
  }

  /* .leaflet-container svg: reset svg max-width decleration shipped in Joomla! (joomla.org) 3.x */
  /* .leaflet-container img: map is broken in FF if you have max-width: 100% on tiles */
  .leaflet-container .leaflet-overlay-pane svg,
  .leaflet-container .leaflet-marker-pane img,
  .leaflet-container .leaflet-shadow-pane img,
  .leaflet-container .leaflet-tile-pane img,
  .leaflet-container img.leaflet-image-layer,
  .leaflet-container .leaflet-tile {
    max-width: none !important;
    max-height: none !important;
  }

  .leaflet-container.leaflet-touch-zoom {
    touch-action: pan-x pan-y;
  }

  .leaflet-container.leaflet-touch-drag {
    /* Fallback for FF which doesn't support pinch-zoom */
    touch-action: none;
    touch-action: pinch-zoom;
  }

  .leaflet-container.leaflet-touch-drag.leaflet-touch-zoom {
    touch-action: none;
  }

  .leaflet-zoom-box {
    width: 0;
    height: 0;
    box-sizing: border-box;
    z-index: 80;
    border: 2px dotted #38f;
    background: rgba(255, 255, 255, 0.5);
  }

  .leaflet-tile-pane {
    z-index: 20;
  }

  .leaflet-overlay-pane,
  .leaflet-pane {
    z-index: 40;
  }

  .leaflet-shadow-pane {
    z-index: 50;
  }

  .leaflet-marker-pane {
    z-index: 60;
  }

  .leaflet-tooltip-pane {
    z-index: 65;
  }

  .leaflet-popup-pane {
    z-index: 70;
    cursor: auto;
  }

  .leaflet-map-pane canvas {
    z-index: 10;
  }

  .leaflet-vml-shape {
    width: 1px;
    height: 1px;
  }

  .lvml {
    behavior: url(#default#VML);
    display: inline-block;
    position: absolute;
  }

  /* control positioning */
  .leaflet-control {
    position: relative;
    z-index: 80;
    pointer-events: auto;
    float: left;
    clear: both;
    cursor: auto;
  }

  .leaflet-top,
  .leaflet-bottom {
    position: absolute;
    z-index: 90;
    pointer-events: none;
  }

  .leaflet-top {
    top: 0;

    .leaflet-control {
      margin-top: ${getSpace('5px')};

      ${mq('m')} {
        margin-top: ${getSpace('xs')};
      }
    }
  }

  .leaflet-right {
    right: 0;

    .leaflet-control {
      margin-right: ${getSpace('5px')};
      float: right;

      ${mq('m')} {
        margin-right: ${getSpace('xs')};
      }
    }
  }

  .leaflet-bottom {
    bottom: 0;

    .leaflet-control {
      margin-bottom: ${getSpace('5px')};

      ${mq('m')} {
        margin-bottom: ${getSpace('xs')};
      }
    }
  }

  .leaflet-left {
    left: 0;

    .leaflet-control {
      margin-left: ${getSpace('5px')};

      ${mq('m')} {
        margin-left: ${getSpace('xs')};
      }
    }
  }

  .leaflet-popup {
    position: absolute;
    text-align: center;
    margin-bottom: 20px;
  }

  /* zoom and fade animations */
  .leaflet-fade-anim .leaflet-tile {
    will-change: opacity;
  }

  .leaflet-fade-anim .leaflet-popup {
    opacity: 0;
    transition: opacity 0.2s linear;
  }

  .leaflet-fade-anim .leaflet-map-pane .leaflet-popup {
    opacity: 1;
  }

  .leaflet-zoom-animated {
    transform-origin: 0 0;
    will-change: transform;
  }

  .leaflet-zoom-anim .leaflet-zoom-animated {
    transition: transform 0.25s cubic-bezier(0, 0, 0.25, 1);
  }

  .leaflet-zoom-anim .leaflet-tile,
  .leaflet-pan-anim .leaflet-tile {
    transition: none;
  }

  .leaflet-zoom-anim .leaflet-zoom-hide {
    visibility: hidden;
  }

  .leaflet-interactive {
    cursor: pointer;
  }

  .leaflet-grab {
    cursor: grab;
  }

  .leaflet-crosshair,
  .leaflet-crosshair .leaflet-interactive {
    cursor: crosshair;
  }

  .leaflet-dragging .leaflet-grab,
  .leaflet-dragging .leaflet-grab .leaflet-interactive,
  .leaflet-dragging .leaflet-marker-draggable {
    cursor: move;
    cursor: grabbing;
  }

  /* marker & overlays interactivity */
  .leaflet-marker-icon,
  .leaflet-marker-shadow,
  .leaflet-image-layer,
  .leaflet-pane > svg path {
    pointer-events: none;
  }

  .leaflet-marker-icon.leaflet-interactive,
  .leaflet-image-layer.leaflet-interactive,
  .leaflet-pane > svg path.leaflet-interactive,
  svg.leaflet-image-layer.leaflet-interactive path {
    pointer-events: visiblePainted; /* IE 9-10 doesn't have auto */
    pointer-events: auto;
  }

  /* general toolbar styles */
  .leaflet-bar {
    ${backgroundShape()}

    a {
      width: 26px;
      height: 26px;
      line-height: 26px;
      display: block;
      text-align: center;
      text-decoration: none;
      color: ${props => props.theme.siteTheme.primary};
    }
  }

  .leaflet-bar a,
  .leaflet-control-layers-toggle {
    background-position: 50% 50%;
    background-repeat: no-repeat;
    display: block;
  }

  .leaflet-bar a.leaflet-disabled {
    cursor: default;
    background-color: #323232;
    color: #bbb;
  }

  .leaflet-touch .leaflet-bar a {
    width: 40px;
    height: 40px;
    line-height: 40px;
  }

  /* zoom control */
  .leaflet-control-zoom-in,
  .leaflet-control-zoom-out {
    font: bold 18px 'Lucida Console', Monaco, monospace;
    text-indent: 1px;
  }

  .leaflet-touch .leaflet-control-zoom-in,
  .leaflet-touch .leaflet-control-zoom-out {
    font-size: 22px;
  }

  /* layers control */
  .leaflet-control-layers {
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
    background: #fff;
    border-radius: 5px;
  }

  .leaflet-control-layers-toggle {
    background-image: url(images/layers.png);
    width: 36px;
    height: 36px;
  }

  .leaflet-retina .leaflet-control-layers-toggle {
    background-image: url(images/layers-2x.png);
    background-size: 26px 26px;
  }

  .leaflet-touch .leaflet-control-layers-toggle {
    width: 44px;
    height: 44px;
  }

  .leaflet-control-layers .leaflet-control-layers-list,
  .leaflet-control-layers-expanded .leaflet-control-layers-toggle {
    display: none;
  }

  .leaflet-control-layers-expanded .leaflet-control-layers-list {
    display: block;
    position: relative;
  }

  .leaflet-control-layers-expanded {
    padding: 6px 10px 6px 6px;
    color: #333;
    background: #fff;
  }

  .leaflet-control-layers-scrollbar {
    overflow-y: scroll;
    overflow-x: hidden;
    padding-right: 5px;
  }

  .leaflet-control-layers-selector {
    margin-top: 2px;
    position: relative;
    top: 1px;
  }

  .leaflet-control-layers label {
    display: block;
  }

  .leaflet-control-layers-separator {
    height: 0;
    border-top: 1px solid #ddd;
    margin: 5px -10px 5px -6px;
  }

  /* Default icon URLs */
  .leaflet-default-icon-path {
    background-image: url(images/marker-icon.png);
  }

  /* attribution and scale controls */
  .leaflet-container .leaflet-control-attribution {
    background: #fff;
    background: rgba(255, 255, 255, 0.7);
    margin: 0;
  }

  .leaflet-control-attribution,
  .leaflet-control-scale-line {
    padding: 0 5px;
    color: #333;

    a {
      display: none;
    }
  }

  .leaflet-container .leaflet-control-attribution,
  .leaflet-container .leaflet-control-scale {
    font-size: 11px;
  }

  .leaflet-left .leaflet-control-scale {
    margin-left: 5px;
  }

  .leaflet-bottom .leaflet-control-scale {
    margin-bottom: 5px;
  }

  .leaflet-control-scale-line {
    border: 2px solid #777;
    border-top: none;
    line-height: 1.1;
    padding: 2px 5px 1px;
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    box-sizing: border-box;

    background: #fff;
    background: rgba(255, 255, 255, 0.5);
  }

  .leaflet-control-scale-line:not(:first-child) {
    border-top: 2px solid #777;
    border-bottom: none;
    margin-top: -2px;
  }

  .leaflet-control-scale-line:not(:first-child):not(:last-child) {
    border-bottom: 2px solid #777;
  }

  .leaflet-touch .leaflet-control-attribution,
  .leaflet-touch .leaflet-control-layers,
  .leaflet-touch .leaflet-bar {
    box-shadow: none;
  }

  .leaflet-touch .leaflet-control-layers,
  .leaflet-touch .leaflet-bar {
    background-clip: padding-box;
  }

  .leaflet-popup-content-wrapper {
    padding: 1px;
    text-align: left;
    color: #fff;
    ${backgroundShape()}
  }

  .leaflet-popup-content {
    margin: 20px;
  }

  .leaflet-popup-tip-container {
    width: 40px;
    height: 20px;
    position: absolute;
    left: 50%;
    margin-left: -20px;
    overflow: hidden;
    pointer-events: none;
  }

  .leaflet-popup-tip {
    width: 17px;
    height: 17px;
    padding: 1px;
    background: #000;
    color: #fff;
    margin: -10px auto 0;
    transform: rotate(45deg);
  }

  .leaflet-container a.leaflet-popup-close-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 4px 4px 0 0;
    border: none;
    text-align: center;
    width: 18px;
    height: 18px;
    color: #fff;
    text-decoration: none;
    font-weight: bold;
    background: transparent;
    font-size: 1.5rem;
    line-height: 18px;
  }

  .leaflet-container a.leaflet-popup-close-button:hover {
    color: ${props => props.theme.globalTheme.primary};
  }

  .leaflet-popup-scrolled {
    overflow: auto;
    border-bottom: 1px solid #ddd;
    border-top: 1px solid #ddd;
  }

  .leaflet-oldie .leaflet-popup-content-wrapper {
    zoom: 1;
  }

  .leaflet-oldie .leaflet-popup-tip {
    width: 24px;
    margin: 0 auto;

    filter: progid:DXImageTransform.Microsoft.Matrix(M11=0.70710678, M12=0.70710678, M21=-0.70710678, M22=0.70710678);
  }

  .leaflet-oldie .leaflet-popup-tip-container {
    margin-top: -1px;
  }

  .leaflet-oldie .leaflet-control-zoom,
  .leaflet-oldie .leaflet-control-layers,
  .leaflet-oldie .leaflet-popup-content-wrapper,
  .leaflet-oldie .leaflet-popup-tip {
    border: 1px solid #999;
  }

  /* div icon */
  .leaflet-div-icon {
    background: #fff;
    border: 1px solid #666;
  }

  /* Tooltip */
  /* Base styles for the element that has a tooltip */
  .leaflet-tooltip {
    position: absolute;
    padding: 6px;
    background-color: #fff;
    border: 1px solid #fff;
    border-radius: 3px;
    color: #222;
    white-space: nowrap;
    user-select: none;
    pointer-events: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  }

  .leaflet-tooltip.leaflet-clickable {
    cursor: pointer;
    pointer-events: auto;
  }

  .leaflet-tooltip-top:before,
  .leaflet-tooltip-bottom:before,
  .leaflet-tooltip-left:before,
  .leaflet-tooltip-right:before {
    position: absolute;
    pointer-events: none;
    border: 6px solid transparent;
    background: transparent;
    content: '';
  }

  /* Directions */
  .leaflet-tooltip-bottom {
    margin-top: 6px;
  }

  .leaflet-tooltip-top {
    margin-top: -6px;
  }

  .leaflet-tooltip-bottom:before,
  .leaflet-tooltip-top:before {
    left: 50%;
    margin-left: -6px;
  }

  .leaflet-tooltip-top:before {
    bottom: 0;
    margin-bottom: -12px;
    border-top-color: #fff;
  }

  .leaflet-tooltip-bottom:before {
    top: 0;
    margin-top: -12px;
    margin-left: -6px;
    border-bottom-color: #fff;
  }

  .leaflet-tooltip-left {
    margin-left: -6px;
  }

  .leaflet-tooltip-right {
    margin-left: 6px;
  }

  .leaflet-tooltip-left:before,
  .leaflet-tooltip-right:before {
    top: 50%;
    margin-top: -6px;
  }

  .leaflet-tooltip-left:before {
    right: 0;
    margin-right: -12px;
    border-left-color: #fff;
  }

  .leaflet-tooltip-right:before {
    left: 0;
    margin-left: -12px;
    border-right-color: #fff;
  }
`;

export const StyledMapSkeleton = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  height: 100%;
  padding-bottom: calc(9 / 16 * 100% - 10px);
  border: 3px solid black;
  background: #fff;

  ${mq('m')} {
    border: 5px solid black;
  }

  span {
    position: absolute;
    width: 100%;
    text-transform: uppercase;
    bottom: 0;
    left: 0;
    padding: 30px 30px 40px;
    ${getTextType('subline')}

    ${mq('m')} {
      width: ${getGridSpan(8)};
      ${getTextType('primary')}
    }

    &:before {
      position: absolute;
      content: '';
      width: 50px;
      height: 3px;
      top: 0px;
      left: -25px;
      background: #000;

      ${mq('m')} {
        height: 5px;
      }
    }
  }
`;
